import { FormBlock } from '@qbe/ads-blocks-forms';
import { Alert, Button, FormGroupLabel, Spacer, Stack } from '@qbe/ads-core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Loading from '~/components-ds/Loading';
import { COVER_DETAILS_LIABILITY_ROUTE } from '~/constants/routes';
import {
  useAppContent,
  useAppDispatch,
  useBusinessDetails,
  useExtraInfo,
  useOptionSelect,
  useSelectCover,
} from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useCoverOptions } from '~/hooks/useCoverOptions';
import { selectIsLiabilityOnlyForPropertyOwner } from '~/selectors/selectCoverOptions';
import { removeInterestedParty } from '~/slices/extraInfo';
import { addCover, removeCover } from '~/slices/selectCover';
import { qalFormCompleteAction, qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { Cover } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { CoverOption } from './CoverOption';
import { CoverSelectorGrid, CoverSelectorGridItem } from './Layout';
import { LiabilityHelper } from './LiabilityHelper';

const CoverSelector: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent } = useAppContent({ basePath: 'dsme.selectCover' });
  const isLiabilityOnlyForPropertyOwner = useSelector(selectIsLiabilityOnlyForPropertyOwner);
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { isFloodZone } = useBusinessDetails();
  const { covers, declinedCovers } = useSelectCover();
  const { interestedParties } = useExtraInfo();
  const { data: coverOptions } = useCoverOptions();

  const { selectedIDs, onSelectID, setSelectedIDs } = useOptionSelect({
    options:
      coverOptions?.map((option) => ({
        id: option.id,
        value: option.cover,
      })) ?? [],
    multiSelect: true,
    defaultOptions: coverOptions ? [0] : [],
  });

  const handleFormStart = useCallback(() => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  }, [dispatch]);

  const handleFormComplete = useCallback(() => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  }, [dispatch]);

  useEffect(() => {
    setSelectedIDs(covers);
  }, [covers, setSelectedIDs]);

  useEffect(() => {
    if (isFloodZone && covers.includes(Cover.BuildingAndContents)) {
      dispatch(removeCover(Cover.BuildingAndContents));
      dispatch(removeCover(Cover.BusinessInterruption));

      dispatch(
        qalTrackEventAction({
          event: 'business_warning',
          eventAction: 'Flood rating zone warning',
          eventLabel:
            'Sorry, we are unable to offer you Business Buildings and Contents and Business Interruption cover as your business address is in a high flood risk location.',
        })
      );
    }
  }, [isFloodZone, covers, dispatch]);

  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    covers.forEach((cover: string) => {
      dispatch(
        qalTrackEventAction({
          event: 'submit',
          eventAction: 'select cover',
          eventLabel: cover.toLowerCase(),
          data: {},
        })
      );
    });
    handleFormComplete();
    e.preventDefault();
    dispatch(
      navigateAction({
        to: COVER_DETAILS_LIABILITY_ROUTE,
        navigate,
      })
    );
  };

  const optionPropsSelector = useMemo(() => {
    const handleSelect = (id: Cover) => {
      if (id === Cover.Liability) return;

      if (!selectedIDs.includes(id)) {
        dispatch(addCover(id));
      } else {
        dispatch(removeCover(id));
        const partiesWithCover = interestedParties?.filter((party) => party.cover === id);
        partiesWithCover?.forEach((party) => dispatch(removeInterestedParty(party.id)));

        /* When we unselect BuildingAndContents -> unselect BI as well */
        if (id === Cover.BuildingAndContents && selectedIDs.includes(Cover.BusinessInterruption)) {
          dispatch(removeCover(Cover.BusinessInterruption));
          setSelectedIDs(
            selectedIDs.filter(
              (s) => ![Cover.BuildingAndContents, Cover.BusinessInterruption].includes(s as Cover)
            )
          );
          return;
        }
      }
      handleFormStart();
      onSelectID(id);
    };

    return coverOptions?.map(({ id }) => {
      const isSelected = !!selectedIDs.find((o) => o === id);
      const isBICoverAllowed =
        !selectedIDs.includes(Cover.BuildingAndContents) && id === Cover.BusinessInterruption;
      const isSoftDeclined = declinedCovers.includes(id);
      const disableFloodRelatedCover =
        isFloodZone && (id === Cover.BuildingAndContents || id === Cover.BusinessInterruption);

      const disabled = isSoftDeclined || isBICoverAllowed || disableFloodRelatedCover!;

      return {
        id,
        isSelected,
        handleSelect,
        disabled,
      };
    });
  }, [
    coverOptions,
    selectedIDs,
    handleFormStart,
    onSelectID,
    dispatch,
    interestedParties,
    setSelectedIDs,
    declinedCovers,
    isFloodZone,
  ]);

  return (
    <>
      <FormGroupLabel title={getTextContent('fields.businessLiability.title')} alignCenter />

      {!optionPropsSelector && <Loading />}

      <CoverSelectorGrid>
        {optionPropsSelector && (
          <CoverSelectorGridItem>
            <CoverOption {...optionPropsSelector[0]} />
          </CoverSelectorGridItem>
        )}
        <CoverSelectorGridItem>
          <LiabilityHelper isLiabilityOnlyForPropertyOwner={isLiabilityOnlyForPropertyOwner} />
        </CoverSelectorGridItem>
      </CoverSelectorGrid>
      <Spacer size="lg" />

      {!isLiabilityOnlyForPropertyOwner && (
        <>
          <FormGroupLabel title={getTextContent('fields.propertyAndRevenue.title')} alignCenter />
          {isFloodZone && (
            <Stack padding={1}>
              <Alert
                variant="warning"
                heading={getTextContent('fields.propertyAndRevenue.floodCoverError')}
              />
            </Stack>
          )}

          {optionPropsSelector ? (
            <CoverSelectorGrid>
              {optionPropsSelector.slice(1).map((props) => (
                <CoverSelectorGridItem key={props.id}>
                  <CoverOption {...props} />
                </CoverSelectorGridItem>
              ))}
            </CoverSelectorGrid>
          ) : (
            <Loading />
          )}
        </>
      )}

      <FormBlock preset="button">
        <Button
          data-track="next"
          onClick={handleNext}
          disabled={!covers}
          label={getSharedContent('nextStepButtonLabel')}
        />
      </FormBlock>
    </>
  );
};

export default CoverSelector;
